import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpOrderService from "@plannel/services/fp/fp-order-service";
import CustomerPopup from "@plannel/pages/factory-plan/common/popup/CustomerPopup";
import OrderTypePopup from "@plannel/pages/factory-plan/common/popup/OrderTypePopup";
import InventoryPopup from "@plannel/pages/factory-plan/common/popup/InventoryPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import ColorPickerRenderer from "@plannel/pages/factory-plan/common/component/ColorPickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 3개(dmndCd/inventoryCd/dueDate) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "dmndCd", label: "FP_SO_CD" },
  { field: "inventoryCd", label: "FP_INVENTORY_CD" },
  { field: "dueDate", label: "FP_DUE_DT" }
];

// TODO(MIG-T2): [order/DemandTab] 고객 담당자(salesPerson)/고객 납기일(customerDueDt) 미이관.
//   old: sales 그룹 하위에 salesPerson(읽기전용)/customerDueDt(읽기전용) 컬럼 존재.
//   사유: 이관된 backend DemandDto에 대응 필드 자체가 없음(backend-gaps.md 등재 대상 아님 —
//   old 화면 자체가 편집 불가한 참고성 표시 컬럼이었고, 1차 목표(조회/저장 동작)에 필수 아님).

/**
 * 판매주문(Demand/Sales Order) 탭 — old DemandTab.jsx 대응. §11-1(항상 마운트 + display 토글)로
 * Order.js에서 렌더링되므로, planScope prop이 바뀔 때마다 이 탭도 자체적으로 재조회한다(§11-3).
 *
 * props: t, viewName(그리드 p13n 키), planScope, searchFields(customerCdNm/inventoryCdNm/dmndCdNm)
 */
const DemandTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [demand, setDemand] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [inventoryPopup, setInventoryPopup] = useState({ open: false, node: null });
  const [customerPopup, setCustomerPopup] = useState({ open: false, node: null, editable: false });
  const [orderTypePopup, setOrderTypePopup] = useState({ open: false, node: null, editable: false });

  const planStrategyValuesRef = useCommonCodeValues("FP_PLAN_STRATEGY");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_DEMAND"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // planScope는 서버 조회, customerCdNm/inventoryCdNm/dmndCdNm은 AG Grid 외부 필터로 즉시 필터
  // (§6-1 케이스2 예외 — Order 화면은 응답에 명칭 컬럼이 없는 dmndCdNm/woCdNm이 있어 old도
  // SearchOptsGroup 카탈로그에서 이 필드들을 예외로 다뤘으나(fp-master-screen-guide.md §6-1 표),
  // 실제로는 dmndCd/customerCd/inventoryCd 모두 이미 로드된 데이터로 클라 필터가 가능해 케이스3으로
  // 처리한다 — 코드만으로 일치하는지 검사(명칭 컬럼이 없는 필드는 코드로만 매칭).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { customerCdNm, inventoryCdNm, dmndCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (customerCdNm && !(hit(r.customerCd, customerCdNm) || hit(r.customerNm, customerCdNm))) return false;
    if (inventoryCdNm && !(hit(r.inventoryCd, inventoryCdNm) || hit(r.inventoryNm, inventoryCdNm))) return false;
    if (dmndCdNm && !hit(r.dmndCd, dmndCdNm)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_SO_CD", field: "dmndCd", colId: "dmndCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_INVENTORY_CD", field: "inventoryCd", colId: "inventoryCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setInventoryPopup({ open: true, node }) } },
    { headerName: "FP_INVENTORY_NAME", field: "inventoryNm", colId: "inventoryNm", type: ["nonEditableColumn"], width: 220 },
    { headerName: "FP_REQUEST_QTY", field: "requestQty", colId: "requestQty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_CUSTOMER_CD", field: "customerCd", colId: "customerCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setCustomerPopup({ open: true, node, editable: false }) } },
    { headerName: "FP_CUSTOMER_NM", field: "customerNm", colId: "customerNm", type: ["nonEditableColumn"], width: 220 },
    { headerName: "FP_DUE_DT", field: "dueDate", colId: "dueDate", width: 160, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_PRIORITY", field: "priority", colId: "priority", width: 100, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110 },
    { headerName: "FP_DISPLAY_COLOR", field: "displayColor", colId: "displayColor", width: 100,
      editable: false, cellRenderer: ColorPickerRenderer, cellStyle: { padding: 0 } },
    { headerName: "FP_DUE_DT_FENCE", field: "dueDateFence", colId: "dueDateFence", width: 160, type: ["dateTimeColumn", "centerAligned"] },
    {
      headerName: "FP_PLAN_POLICY", groupId: "planPolicy", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_ORDER_STRATEGY", field: "orderStrategyTpCd", colId: "orderStrategyTpCd", width: 110, type: ["centerAligned"],
          cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
          cellEditorParams: () => codeComboCellEditorParams("FP_PLAN_STRATEGY", planStrategyValuesRef, t) },
        { headerName: "FP_PST", field: "pst", colId: "pst", width: 160,  type: ["dateTimeColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "FP_EFFICIENCY", field: "efficiency", colId: "efficiency", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: "FP_ORDER_TP_CD", field: "orderTpCd", colId: "orderTpCd", cellClass: "stringType", width: 130, columnGroupShow: "open",
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setOrderTypePopup({ open: true, node, editable: false }) } }
      ]
    },
    {
      headerName: "FP_EXTRA_INFO", groupId: "extraInfo", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_ACTIVE_YN", field: "activeYn", colId: "activeYn", width: 100, type: ["booleanColumn", "centerAligned"] },
        { headerName: "FP_DMND_GRP_CD", field: "dmndGrpCd", colId: "dmndGrpCd", cellClass: "stringType", width: 130, columnGroupShow: "open" },
        { headerName: "FP_GRP_PRIORITY", field: "grpPriority", colId: "grpPriority", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" }
      ]
    },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveDemand = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpOrderService.getDemands({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setDemand([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setDemand(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          gridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveDemand();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, dmndCd: "", inventoryCd: "", inventoryNm: "", requestQty: 0,
      customerCd: "", customerNm: "", dueDate: null, priority: 1, descTxt: "",
      displayColor: "#ff96c8", dueDateFence: null, orderStrategyTpCd: "FORWARD", pst: null,
      efficiency: 1, orderTpCd: "", activeYn: true, dmndGrpCd: "", grpPriority: 1
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeDemand = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpOrderService.removeDemands(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveDemand());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveDemand = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }
    
    console.log(allStateData);

    fpOrderService.upsertDemands(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveDemand());
  };

  const onSelectInventory = (row) => {
    const node = inventoryPopup.node;
    if (node) {
      node.setDataValue("inventoryCd", row.inventoryCd);
      node.setDataValue("inventoryNm", row.inventoryNm);
    }
  };

  const onSelectCustomer = (row) => {
    const node = customerPopup.node;
    if (node) {
      node.setDataValue("customerCd", row.customerCd);
      node.setDataValue("customerNm", row.customerNm);
    }
  };

  // old setOrderTypeValues — orderTpCd 선택 시 정책값(계획방식/효율)까지 함께 반영.
  const onSelectOrderType = (row) => {
    const node = orderTypePopup.node;
    if (node) {
      node.setDataValue("orderTpCd", row.orderTpCd);
      node.setDataValue("orderStrategyTpCd", row.orderStrategyTpCd);
      node.setDataValue("pst", row.orderPst);
      node.setDataValue("efficiency", row.orderEfficiency);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveDemand();
    else if (action === "DELETE") removeDemand();
  };

  // 화면 내부 탭(§11-3): planScope가 바뀔 때만 재조회 — 탭 전환 자체는 재조회를 트리거하지 않음.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveDemand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex" }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_DEMAND")}.xlsx`, sheetName: t("FP_DEMAND") })} />
          <Button size="small" onClick={() => setOrderTypePopup({ open: true, node: null, editable: true })}>
            {t("FP_ORDER_TYPE_MST")}
          </Button>
          <Button size="small" onClick={() => setCustomerPopup({ open: true, node: null, editable: true })}>
            {t("FP_CUSTOMER_MST")}
          </Button>
        </Box>
        <Box sx={{ display: "flex" }}>
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </Box>
      <Box id="fp-demand-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={demand}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <InventoryPopup
        open={inventoryPopup.open}
        onClose={() => setInventoryPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectInventory}
      />
      <CustomerPopup
        open={customerPopup.open}
        onClose={() => setCustomerPopup({ open: false, node: null, editable: false })}
        planScope={planScope}
        onSelect={onSelectCustomer}
        editable={customerPopup.editable}
      />
      <OrderTypePopup
        open={orderTypePopup.open}
        onClose={() => setOrderTypePopup({ open: false, node: null, editable: false })}
        planScope={planScope}
        onSelect={onSelectOrderType}
        editable={orderTypePopup.editable}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(DemandTab);
